import axios, { AxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/store/authentication'
import { useNotificationStore } from '@/store/notification'
import { useAppStore } from '@/store/app'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import router from '@/router'
import { useModuleStore } from '@/store/module'
import { openModal } from '@/utils/modalController'
import TwoFactorAuthTab from '@/components/partials/profile/TwoFactorAuthTab.vue'
import { useVerificationStore } from '@/store/verification'
import { useInterruptedRequestStore } from '@/store/interruptedRequest'

export type MyZoneRequestConfig = AxiosRequestConfig & {
    withAppLoader?: boolean
    skipAuthRefresh?: boolean
    skipRefreshRetry?: boolean
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL + '/api/admin/'

// the session was not issued by a login that passed two-factor verification; refreshing keeps that, so only a new login helps
const SECOND_FACTOR_REQUIRED = 'auth.second-factor-required'

// the login page shows the outcome of a login itself, so these requests raise no notifications
const isLoginRequest = (config?: AxiosRequestConfig) => !!config?.url?.startsWith('auth/login')

const api = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
})

function refreshAuthLogic(error) {
    const authStore = useAuthStore()
    const originalRequest = error?.response?.config as MyZoneRequestConfig | undefined

    if (
        originalRequest?.url?.includes('login') ||
        originalRequest?.url?.includes('logout') ||
        originalRequest?.url?.includes('/presence/')
    ) {
        return Promise.reject(error)
    }

    if (!authStore.isAuthenticated()) {
        authStore.logout()

        return Promise.reject(error)
    }

    if (error?.response?.data?.message_code === SECOND_FACTOR_REQUIRED) {
        authStore.logout(error.response.data.message ?? null)

        return Promise.reject(error)
    }

    if (!originalRequest || originalRequest.skipAuthRefresh) {
        return Promise.reject(error)
    }

    authStore.refreshing = true

    return authStore.refreshAccessToken().then(() => {
        const latestToken = authStore.getAccessToken()

        if (latestToken) {
            originalRequest.headers.Authorization = 'Bearer ' + latestToken
        } else {
            delete originalRequest.headers.Authorization
        }

        return Promise.resolve()
    }).catch((error) => {
        authStore.logout()

        return Promise.reject(error)
    })
}

api.interceptors.request.use((config: MyZoneRequestConfig) => {
    const appStore = useAppStore()
    const authStore = useAuthStore()

    let withAppLoader = config.withAppLoader

    if (withAppLoader === undefined && config.data && typeof config.data === 'object' && !Array.isArray(config.data)) {
        if ('withAppLoader' in config.data) {
            withAppLoader = config.data.withAppLoader
            delete config.data.withAppLoader
        }
    }

    if (withAppLoader === undefined) {
        withAppLoader = true
    }

    if (appStore && withAppLoader) appStore.apiLoading = true

    if (!['users/verify-code', 'presence', 'auth/'].some(fragment => config.url?.includes(fragment))) {
        const abortController = appStore?.abortController

        config.signal = abortController && !abortController.signal.aborted
            ? abortController.signal
            : undefined
        appStore.abortedRequest.url = config.url
        appStore.abortedRequest.method = config.method?.toString()
        appStore.abortedRequest.data = config.data
        appStore.abortedRequest.headers = config.headers

        if (appStore.apiRequiresVerification) {
            appStore.apiLoading = false
            appStore.abortController?.abort()

            return Promise.reject(new Error('Verification required'))
        }
    } else {
        config.signal = undefined
    }

    const accessToken = authStore.getAccessToken()

    if (accessToken) {
        config.headers.Authorization = 'Bearer ' + accessToken
    } else {
        delete config.headers.Authorization
    }

    if (localStorage.getItem('action_token')) {
        config.headers.common['X-Action-Token'] = localStorage.getItem('action_token')
        // localStorage.removeItem('action_token')      //do not remove item, login gets messy after incorrect code
    }
    const verificationStore = useVerificationStore()
    const verificationUuid = verificationStore.getVerificationUuid()

    if (verificationUuid) {
        config.headers.common.verificationUuid = verificationUuid
    } else {
        delete config.headers.common.verificationUuid
    }

    return config
})

api.interceptors.response.use(async response => {
    const notificationStore = useNotificationStore()
    const isAnswerChallengeRequest = response.config.url?.includes('answer-challenge')
    // Blob responses (e.g. file exports with responseType: 'blob') keep their body as a
    // Blob, so challenge detection below can't read `challengesPending`. Decode a JSON blob
    // into a plain object for detection only — `response.data` is left untouched for the
    // download path when there is no pending challenge.
    const challengeData = await resolveChallengeData(response.data)
    const challengePayload = extractChallengePayload(challengeData)
    const hasPendingChallenges = challengeData?.challengesPending === true || challengePayload.challengesPending === true
    const isConfigureChallengePayload = challengePayload.actionResults.length > 0 &&
        challengePayload.actionResults.every(result => result.action === 'configure')
    const verificationStore = useVerificationStore()

    if (hasPendingChallenges && !isConfigureChallengePayload) {
        const { actionResults, verificationUuid } = challengePayload

        verificationStore.requiresVerification = true
        if (verificationUuid) {
            verificationStore.setVerificationUuid(verificationUuid)
        }

        if (!isAnswerChallengeRequest) {
            verificationStore.actionResults = actionResults

            const interruptedRequestStore = useInterruptedRequestStore()
            const pendingRequestPromise = interruptedRequestStore.captureRequest(response.config)

            useAppStore().apiLoading = false

            return pendingRequestPromise
        }
    }

    const challengesCleared = challengeData?.challengesPending === false || challengePayload.challengesPending === false

    if (
        challengesCleared &&
        !isAnswerChallengeRequest
    ) {
        const verificationStore = useVerificationStore()
        const { actionResults } = challengePayload

        verificationStore.requiresVerification = false
        verificationStore.actionResults = actionResults
    }

    if(response.data?.data?.confirmAction){
        useAppStore().confirmAction = response.data.data.confirmAction
        useAppStore().confirmAction.requestConfig = response.config
        response.data = useModuleStore().detailData
    }

    if (response.status === 200) {
        useAuthStore().refreshing = false
    }

    if (response.data.message && !isLoginRequest(response.config)) {
        notificationStore.addNotification({
            id: Math.random().toString(36).slice(2, 7),
            type: 'success',
            message: response.data.message,
        })
    }

    if (response.data.redirect_url) {
        if (router.currentRoute.value.path !== response.data.redirect_url)
            router.push(response.data.redirect_url)
        else
            window.location.reload()
    }

    useAppStore().apiLoading = false

    return response
}, async (error) => {
    const appStore = useAppStore()
    const notificationStore = useNotificationStore()

    appStore.apiLoading = false

    if (error.message === 'canceled') {
        return Promise.resolve()
    }

    if (error?.response?.data?.requires_2fa) {
        const interruptedRequestStore = useInterruptedRequestStore()
        const pendingRequestPromise = interruptedRequestStore.captureRequest(error?.config)

        openModal({
            title: 'Two-Factor Required',
            component: TwoFactorAuthTab,
            disableClose: true,
        })
        
        return pendingRequestPromise
    }

    if (error.response && error.response.status === 404) {
        router.go(-1)
        notificationStore.setErrors(error.response.data.errors)
    }

    if (error.response && error.response.status === 422) {
        const errors = error.response.data?.errors

        // a 422 without field errors is a refused action (e.g. close account), not a form to fix
        if (errors && Object.keys(errors).length) {
            useModuleStore().editEnabled = true
            notificationStore.setErrors(errors)
        }
    }

    if (error.response && error.response.data.message && error.response.data.message !== 'Unauthenticated.' && !isLoginRequest(error.config)) {
        notificationStore.addNotification({
            id: Math.random().toString(36).slice(2, 7),
            type: 'error',
            message: error.response.data.message,
        })
    }

    return Promise.reject(error)
})

createAuthRefreshInterceptor(api, refreshAuthLogic)

export default api
// Normalises a response body into a plain object usable for challenge detection.
// For Blob bodies (responseType: 'blob') it attempts to decode a JSON payload so that a
// pending step-up verification returned as JSON isn't silently treated as a downloadable file.
const resolveChallengeData = async (data: unknown): Promise<Record<string, unknown> | undefined> => {
    if (!(data instanceof Blob)) {
        return data as Record<string, unknown> | undefined
    }

    // Real file exports are non-JSON; only try to decode when the blob looks like JSON.
    if (data.type && !data.type.includes('json')) {
        return undefined
    }

    try {
        const text = await data.text()
        const parsed = JSON.parse(text)

        return typeof parsed === 'object' && parsed !== null
            ? parsed as Record<string, unknown>
            : undefined
    } catch {
        return undefined
    }
}

const extractChallengePayload = (payload: Record<string, unknown> | undefined) => {
    const root = typeof payload === 'object' && payload !== null
        ? payload as Record<string, unknown>
        : {
        }
    const nestedCandidate = 'data' in root ? root.data : undefined
    const nested = typeof nestedCandidate === 'object' && nestedCandidate !== null
        ? nestedCandidate as Record<string, unknown>
        : undefined

    const lookup = (...keys: Array<string>) => {
        for (const key of keys) {
            if (key in (root as Record<string, unknown>)) {
                return (root as Record<string, unknown>)[key]
            }
            if (nested && key in nested) {
                return nested[key]
            }
        }

        return undefined
    }

    const actionResults = lookup('actionResults', 'action_results')
    const verificationUuid = lookup('verificationUuid', 'verification_uuid')
    const challengesPending = lookup('challengesPending', 'challenges_pending')

    return {
        actionResults: Array.isArray(actionResults) ? actionResults : [],
        verificationUuid: typeof verificationUuid === 'string' ? verificationUuid : null,
        challengesPending: typeof challengesPending === 'boolean' ? challengesPending : undefined,
    }
}
